import { useTranslation } from 'react-i18next';
import type { ProjectionWarning } from '@dairy/domain';
import type { ResolvedProjectionAssumptions } from '@dairy/shared';
import { Card, CardTitle } from '@/components/ui';
import { useFormat } from '@/lib/format';
import { assumptionKeys, inputValue } from './assumption-form';

export function ProjectionNotices({ warnings = [] }: { warnings?: ProjectionWarning[] }) {
  const { t } = useTranslation();
  return (
    <Card className="space-y-2 border-amber-200 bg-amber-50 text-amber-950" role="note">
      <h2 className="font-bold">{t('projectionForm.estimateNotice')}</h2>
      <ul className="space-y-1 text-sm">
        <li>{t('projectionForm.marginalNotice')}</li>
        <li>{t('projectionForm.calfNotice')}</li>
        <li>{t('projectionForm.healthNotice')}</li>
      </ul>
      {warnings.length ? (
        <ul
          aria-label={t('projectionForm.checkNumbers')}
          className="space-y-2 border-t border-amber-200 pt-3 font-semibold"
        >
          {warnings.map((warning) => (
            <li key={warning}>{t(`projectionForm.warnings.${warning}`)}</li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
export function ProjectionAssumptionDetails({ assumptions }: { assumptions: ResolvedProjectionAssumptions }) {
  const { t } = useTranslation();
  const f = useFormat();
  return (
    <Card>
      <CardTitle>{t('projectionForm.assumptionsUsed')}</CardTitle>
      <dl className="divide-y divide-stone-100">
        {assumptionKeys.map((key) => {
          const value = assumptions[key];
          return (
            <div key={key} className="grid gap-1 py-3 sm:grid-cols-2">
              <dt>{t(`projections.fields.${key}`)}</dt>
              <dd className="text-sm">
                {inputValue(key, value.value)} · {t(`projections.sources.${value.source}`)}
                {value.from && value.to ? ` · ${f.short(value.from)} – ${f.short(value.to)}` : ''}
                {value.sample !== undefined
                  ? ` · ${t(`projections.samples.${value.sampleUnit}`, { count: value.sample, defaultValue: t('projections.sample', { count: value.sample }) })}`
                  : ''}
              </dd>
            </div>
          );
        })}
      </dl>
      <details className="mt-3">
        <summary className="min-h-12 cursor-pointer py-2 font-semibold">{t('projections.advanced')}</summary>
        {(['low', 'high'] as const).map((kind) => (
          <div key={kind} className="mt-3">
            <h3 className="font-semibold">{t(`projections.scenarios.${kind}`)}</h3>
            <dl className="grid gap-2 py-2 sm:grid-cols-2">
              {(['yieldBp', 'priceBp', 'feedBp', 'spcAddX10'] as const).map((key) => (
                <div key={key}>
                  <dt className="text-sm text-stone-600">{t(`projections.multipliers.${key}`)}</dt>
                  <dd>
                    {key === 'spcAddX10'
                      ? (assumptions.scenarios[kind][key] / 10).toFixed(1)
                      : `${(assumptions.scenarios[kind][key] / 100).toFixed(2)}%`}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </details>
    </Card>
  );
}
